import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  CirclePause,
  CirclePlay,
  Clock3,
  Columns3,
  Eye,
  Pencil,
  Plus,
  Redo2,
  Search,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest, type TokenGetter } from "../api";
import {
  addDays,
  adjustTaskRange,
  applyTaskPatch,
  compareTaskTimelineOrder,
  createTaskHistoryPatches,
  dateKey,
  diffDays,
  mondayOf,
  moveTaskRange,
  taskRange,
  taskRangePatch,
  type TaskDateRange,
  type TimelineDragMode,
} from "./task-utils";
import type { TaskDraft, TaskItem, TaskMeta, TaskStatus } from "./types";

const statusOptions: Array<{ value: TaskStatus; label: string; icon: typeof CircleDashed }> = [
  { value: "on_hold", label: "保留", icon: CirclePause },
  { value: "not_started", label: "未着手", icon: CircleDashed },
  { value: "in_progress", label: "着手中", icon: CirclePlay },
  { value: "waiting_external", label: "外部待ち", icon: Clock3 },
  { value: "waiting_review", label: "レビュー待ち", icon: Eye },
  { value: "completed", label: "完了", icon: CheckCircle2 },
];

const emptyMeta: TaskMeta = { projects: [], categories: [], goals: [] };
const TASK_HISTORY_LIMIT = 100;

interface TaskHistoryEntry {
  taskId: string;
  taskTitle: string;
  before: Partial<TaskDraft>;
  after: Partial<TaskDraft>;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

function emptyDraft(status: TaskStatus): TaskDraft {
  return {
    title: "",
    notes: "",
    tags: "",
    projectId: null,
    goalId: null,
    categoryIds: [],
    due: null,
    scheduled: dateKey(new Date()),
    timelineStart: null,
    timelineEnd: null,
    status,
    priority: 2,
  };
}

function taskDraft(task: TaskItem): TaskDraft {
  return {
    title: task.title,
    notes: task.notes,
    tags: task.tags,
    projectId: task.projectId,
    goalId: task.goalId,
    categoryIds: task.categoryIds,
    due: task.due,
    scheduled: task.scheduled,
    timelineStart: task.timelineStart,
    timelineEnd: task.timelineEnd,
    status: task.status,
    priority: task.priority,
  };
}

function formatShortDate(value: string | null) {
  if (!value) return "未設定";
  const [, month, day] = value.split("-");
  return `${Number(month)}/${Number(day)}`;
}

const TaskCard = memo(function TaskCard({ task, meta, canWrite, onEdit, onUpdate }: {
  task: TaskItem;
  meta: TaskMeta;
  canWrite: boolean;
  onEdit: (task: TaskItem) => void;
  onUpdate: (id: string, patch: Partial<TaskDraft>) => Promise<void>;
}) {
  const range = taskRange(task);
  return (
    <article
      className={`task-card priority-${task.priority}`}
      draggable={canWrite}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/task-id", task.id);
        event.currentTarget.classList.add("dragging");
      }}
      onDragEnd={(event) => event.currentTarget.classList.remove("dragging")}
    >
      <button className="task-card-main" onClick={() => onEdit(task)}>
        <span className="task-card-title">{task.title}</span>
        <span className="task-card-meta">
          {task.projectName && <span><i style={{ backgroundColor: task.projectColor ?? "#64748b" }} />{task.projectName}</span>}
          {range && <span><CalendarDays size={12} />{formatShortDate(range.start)}{range.end !== range.start ? `–${formatShortDate(range.end)}` : ""}</span>}
        </span>
        {task.categoryIds.length > 0 && (
          <span className="task-category-list">
            {task.categoryIds.slice(0, 3).map((id) => <small key={id}>{meta.categories.find((item) => item.id === id)?.name ?? "分類"}</small>)}
          </span>
        )}
      </button>
      {canWrite && (
        <label className="task-card-status">
          <select
            className="task-status-select"
            value={task.status}
            aria-label={`${task.title}のステータス`}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => void onUpdate(task.id, { status: event.target.value as TaskStatus })}
          >
            {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      )}
    </article>
  );
});

function TaskBoard({ tasks, meta, canWrite, onEdit, onUpdate }: {
  tasks: TaskItem[];
  meta: TaskMeta;
  canWrite: boolean;
  onEdit: (task: TaskItem) => void;
  onUpdate: (id: string, patch: Partial<TaskDraft>) => Promise<void>;
}) {
  const rowsByStatus = useMemo(() => {
    const grouped: Record<TaskStatus, TaskItem[]> = {
      on_hold: [],
      not_started: [],
      in_progress: [],
      waiting_external: [],
      waiting_review: [],
      completed: [],
    };
    tasks.forEach((task) => grouped[task.status].push(task));
    return grouped;
  }, [tasks]);

  return (
    <section className="task-board" aria-label="タスクボード">
      {statusOptions.map(({ value, label, icon: Icon }) => {
        const rows = rowsByStatus[value];
        return (
          <section
            className={`task-column task-column-${value}`}
            key={value}
            onDragOver={(event) => canWrite && event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const id = event.dataTransfer.getData("text/task-id");
              if (canWrite && id) void onUpdate(id, { status: value });
            }}
          >
            <header><span><Icon size={15} />{label}</span><strong>{rows.length}</strong></header>
            <div className="task-column-list">
              {rows.map((task) => <TaskCard key={task.id} task={task} meta={meta} canWrite={canWrite} onEdit={onEdit} onUpdate={onUpdate} />)}
              {rows.length === 0 && <p className="task-column-empty">ここにタスクを移動できます</p>}
            </div>
          </section>
        );
      })}
    </section>
  );
}

const TimelineBar = memo(function TimelineBar({ task, visibleStart, canWrite, onEdit, onUpdate }: {
  task: TaskItem;
  visibleStart: string;
  canWrite: boolean;
  onEdit: (task: TaskItem) => void;
  onUpdate: (id: string, patch: Partial<TaskDraft>) => Promise<void>;
}) {
  const range = taskRange(task)!;
  const barRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    mode: TimelineDragMode;
    startX: number;
    dayWidth: number;
    base: TaskDateRange;
    baseWidth: number;
    amount: number;
    moved: boolean;
  } | null>(null);
  const ignoreClickRef = useRef(false);
  const visibleEnd = addDays(visibleStart, 41);
  const clippedStart = range.start < visibleStart ? visibleStart : range.start;
  const clippedEnd = range.end > visibleEnd ? visibleEnd : range.end;
  const column = diffDays(visibleStart, clippedStart) + 1;
  const span = Math.max(1, diffDays(clippedStart, clippedEnd) + 1);

  function resetDragVisual() {
    if (!barRef.current) return;
    barRef.current.classList.remove("dragging");
    barRef.current.style.removeProperty("transform");
    barRef.current.style.removeProperty("width");
    barRef.current.style.removeProperty("will-change");
    delete barRef.current.dataset.dragRange;
  }

  function beginDrag(event: React.PointerEvent, mode: TimelineDragMode) {
    if (!canWrite || event.button !== 0 || !barRef.current) return;
    const track = barRef.current.parentElement;
    if (!track) return;
    event.preventDefault();
    event.stopPropagation();
    barRef.current.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      mode,
      startX: event.clientX,
      dayWidth: track.getBoundingClientRect().width / 42,
      base: range,
      baseWidth: barRef.current.getBoundingClientRect().width,
      amount: 0,
      moved: false,
    };
    barRef.current.classList.add("dragging");
    barRef.current.style.willChange = "transform, width";
    barRef.current.dataset.dragRange = `${formatShortDate(range.start)}–${formatShortDate(range.end)}`;
  }

  function moveDrag(event: React.PointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const rawDelta = event.clientX - drag.startX;
    const minDays = drag.mode === "move"
      ? diffDays(drag.base.end, visibleStart)
      : drag.mode === "start"
        ? diffDays(drag.base.start, visibleStart)
        : diffDays(drag.base.end, drag.base.start);
    const maxDays = drag.mode === "move"
      ? diffDays(drag.base.start, visibleEnd)
      : drag.mode === "start"
        ? diffDays(drag.base.start, drag.base.end)
        : diffDays(drag.base.end, visibleEnd);
    const delta = Math.max(minDays * drag.dayWidth, Math.min(maxDays * drag.dayWidth, rawDelta));
    const amount = Math.round(delta / drag.dayWidth);

    drag.moved = drag.moved || Math.abs(rawDelta) >= 3;
    if (barRef.current) {
      if (drag.mode === "move") {
        barRef.current.style.transform = `translate3d(${delta}px, 0, 0)`;
      } else if (drag.mode === "start") {
        barRef.current.style.transform = `translate3d(${delta}px, 0, 0)`;
        barRef.current.style.width = `${Math.max(drag.dayWidth - 6, drag.baseWidth - delta)}px`;
      } else {
        barRef.current.style.width = `${Math.max(drag.dayWidth - 6, drag.baseWidth + delta)}px`;
      }
    }
    if (amount !== drag.amount && barRef.current) {
      const next = adjustTaskRange(drag.base, drag.mode, amount);
      barRef.current.dataset.dragRange = `${formatShortDate(next.start)}–${formatShortDate(next.end)}`;
    }
    drag.amount = amount;
  }

  function finishDrag(event: React.PointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const next = adjustTaskRange(drag.base, drag.mode, drag.amount);
    dragRef.current = null;
    barRef.current?.releasePointerCapture(event.pointerId);
    if (drag.moved) ignoreClickRef.current = true;
    resetDragVisual();
    if (drag.amount !== 0) {
      void onUpdate(task.id, taskRangePatch(task, next));
    }
  }

  function cancelDrag(event: React.PointerEvent<HTMLButtonElement>) {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    resetDragVisual();
  }

  async function keyboardMove(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (!canWrite || !["ArrowLeft", "ArrowRight"].includes(event.key)) return;
    event.preventDefault();
    const amount = event.key === "ArrowLeft" ? -1 : 1;
    if (event.altKey) {
      const next = addDays(range.start, amount);
      if (next > range.end) return;
      await onUpdate(task.id, { scheduled: next });
    } else if (event.shiftKey) {
      const next = addDays(range.end, amount);
      if (next < range.start) return;
      await onUpdate(task.id, { due: next });
    } else {
      await onUpdate(task.id, moveTaskRange(task, amount));
    }
  }

  return (
    <button
      ref={barRef}
      className={`task-timeline-bar task-timeline-bar-${task.status}`}
      style={{ gridColumn: `${column} / span ${span}` }}
      onPointerDown={(event) => beginDrag(event, "move")}
      onPointerMove={moveDrag}
      onPointerUp={finishDrag}
      onPointerCancel={cancelDrag}
      onClick={() => {
        if (ignoreClickRef.current) { ignoreClickRef.current = false; return; }
        onEdit(task);
      }}
      onKeyDown={(event) => void keyboardMove(event)}
      aria-label={`${task.title}。${formatShortDate(range.start)}から${formatShortDate(range.end)}。矢印キーで移動、Altと矢印で開始日、Shiftと矢印で終了日を変更`}
      title={`${task.title}（矢印: 移動 / Alt+矢印: 開始 / Shift+矢印: 終了）`}
    >
      {canWrite && <span className="task-timeline-resize task-timeline-resize-start" aria-hidden="true" onPointerDown={(event) => beginDrag(event, "start")} />}
      <span className="task-timeline-bar-title">{task.title}</span>
      <small>{formatShortDate(range.start)}–{formatShortDate(range.end)}</small>
      {canWrite && <span className="task-timeline-resize task-timeline-resize-end" aria-hidden="true" onPointerDown={(event) => beginDrag(event, "end")} />}
    </button>
  );
});

const TaskTimelineRow = memo(function TaskTimelineRow({ task, days, today, visibleStart, canWrite, onEdit, onUpdate }: {
  task: TaskItem;
  days: string[];
  today: string;
  visibleStart: string;
  canWrite: boolean;
  onEdit: (task: TaskItem) => void;
  onUpdate: (id: string, patch: Partial<TaskDraft>) => Promise<void>;
}) {
  return (
    <div className="task-timeline-row">
      <button className="task-timeline-label" onClick={() => onEdit(task)}>
        <strong>{task.title}</strong><small>{task.projectName ?? "プロジェクト未設定"}</small>
      </button>
      <div className="task-timeline-track">
        {days.map((day, index) => (
          <span key={day} style={{ gridColumn: index + 1 }} className={day === today ? "today" : ""} />
        ))}
        <TimelineBar task={task} visibleStart={visibleStart} canWrite={canWrite} onEdit={onEdit} onUpdate={onUpdate} />
      </div>
    </div>
  );
});

function TaskTimeline({ tasks, canWrite, onEdit, onUpdate }: {
  tasks: TaskItem[];
  canWrite: boolean;
  onEdit: (task: TaskItem) => void;
  onUpdate: (id: string, patch: Partial<TaskDraft>) => Promise<void>;
}) {
  const [visibleStart, setVisibleStart] = useState(() => mondayOf(new Date()));
  const days = useMemo(() => Array.from({ length: 42 }, (_, index) => addDays(visibleStart, index)), [visibleStart]);
  const visibleEnd = days.at(-1)!;
  const today = dateKey(new Date());
  const { scheduled, unscheduled } = useMemo(() => {
    const scheduledTasks = tasks.filter((task) => {
      const range = taskRange(task);
      return range && range.start <= visibleEnd && range.end >= visibleStart;
    }).sort(compareTaskTimelineOrder);
    return { scheduled: scheduledTasks, unscheduled: tasks.filter((task) => !taskRange(task)) };
  }, [tasks, visibleEnd, visibleStart]);

  return (
    <>
      <div className="task-timeline-controls">
        <button className="button button-quiet" onClick={() => setVisibleStart(addDays(visibleStart, -14))}><ArrowLeft size={15} />2週前</button>
        <button className="button" onClick={() => setVisibleStart(mondayOf(new Date()))}>今日へ戻る</button>
        <span>{visibleStart.replaceAll("-", ".")} — {visibleEnd.replaceAll("-", ".")}</span>
        <button className="button button-quiet" onClick={() => setVisibleStart(addDays(visibleStart, 14))}>2週後<ArrowRight size={15} /></button>
      </div>
      {canWrite && <p className="task-timeline-hint">バー中央をドラッグすると期間を移動、左右端をドラッグすると開始日・終了日を変更できます。クリックすると日付を直接入力できます。⌘Zで取り消し、⌘⇧Zでやり直せます。</p>}
      <section className="task-timeline-shell" aria-label="42日間のタスクタイムライン">
        <div className="task-timeline-inner">
          <div className="task-timeline-header">
            <strong>タスク</strong>
            <div className="task-timeline-days">
              {days.map((day, index) => {
                const date = new Date(`${day}T12:00:00`);
                const weekend = [0, 6].includes(date.getDay());
                const isToday = day === today;
                return <span key={day} style={{ gridColumn: index + 1 }} className={`${weekend ? "weekend" : ""} ${isToday ? "today" : ""}`} aria-current={isToday ? "date" : undefined} title={isToday ? "今日" : undefined}><small>{date.toLocaleDateString("ja-JP", { weekday: "short" })}</small>{date.getDate()}</span>;
              })}
            </div>
          </div>
          {scheduled.map((task) => (
            <TaskTimelineRow key={task.id} task={task} days={days} today={today} visibleStart={visibleStart} canWrite={canWrite} onEdit={onEdit} onUpdate={onUpdate} />
          ))}
          {scheduled.length === 0 && <p className="task-timeline-empty">この期間に配置されたタスクはありません。</p>}
        </div>
      </section>
      {unscheduled.length > 0 && (
        <section className="task-unscheduled">
          <header><div><h2>日付未設定</h2><p>編集画面で期間または配置日を設定するとタイムラインに表示されます。</p></div><strong>{unscheduled.length}</strong></header>
          <div>{unscheduled.map((task) => <button key={task.id} onClick={() => onEdit(task)}>{task.title}<Pencil size={14} /></button>)}</div>
        </section>
      )}
    </>
  );
}

function TaskEditor({ task, initialStatus, meta, canWrite, saving, onClose, onSave, onDelete }: {
  task: TaskItem | null;
  initialStatus: TaskStatus;
  meta: TaskMeta;
  canWrite: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (draft: TaskDraft) => Promise<void>;
  onDelete: (task: TaskItem) => Promise<void>;
}) {
  const [draft, setDraft] = useState<TaskDraft>(() => task ? taskDraft(task) : emptyDraft(initialStatus));
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const setDate = (key: "due" | "scheduled" | "timelineStart" | "timelineEnd", value: string) => setDraft({ ...draft, [key]: value || null });

  return (
    <div className="task-dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="task-dialog" role="dialog" aria-modal="true" aria-labelledby="task-dialog-title">
        <header><div><p>{task ? "TASK DETAIL" : "NEW TASK"}</p><h2 id="task-dialog-title">{task ? "タスクを編集" : "タスクを追加"}</h2></div><button className="icon-button" onClick={onClose} aria-label="閉じる"><X size={19} /></button></header>
        <form onSubmit={(event) => { event.preventDefault(); void onSave(draft); }}>
          <label className="task-field task-field-wide">タイトル<input className="input" autoFocus value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} required maxLength={240} disabled={!canWrite} /></label>
          <div className="task-form-grid">
            <label className="task-field">状態<select className="input" value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as TaskStatus })} disabled={!canWrite}>{statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
            <label className="task-field">優先度<select className="input" value={draft.priority} onChange={(event) => setDraft({ ...draft, priority: Number(event.target.value) })} disabled={!canWrite}><option value={1}>高</option><option value={2}>中</option><option value={3}>低</option></select></label>
            <label className="task-field">プロジェクト<select className="input" value={draft.projectId ?? ""} onChange={(event) => setDraft({ ...draft, projectId: event.target.value || null })} disabled={!canWrite}><option value="">未設定</option>{meta.projects.map((project) => <option value={project.id} key={project.id}>{project.name}</option>)}</select></label>
            <label className="task-field">目標<select className="input" value={draft.goalId ?? ""} onChange={(event) => setDraft({ ...draft, goalId: event.target.value || null })} disabled={!canWrite}><option value="">未設定</option>{meta.goals.map((goal) => <option value={goal.id} key={goal.id}>{goal.kind} · {goal.title}</option>)}</select></label>
            <label className="task-field">開始日<input className="input" type="date" value={draft.scheduled ?? ""} onChange={(event) => setDate("scheduled", event.target.value)} disabled={!canWrite} /></label>
            <label className="task-field">期限<input className="input" type="date" value={draft.due ?? ""} onChange={(event) => setDate("due", event.target.value)} disabled={!canWrite} /></label>
          </div>
          <fieldset className="task-categories" disabled={!canWrite}><legend>分類</legend><div>{meta.categories.map((category) => <label key={category.id}><input type="checkbox" checked={draft.categoryIds.includes(category.id)} onChange={(event) => setDraft({ ...draft, categoryIds: event.target.checked ? [...draft.categoryIds, category.id] : draft.categoryIds.filter((id) => id !== category.id) })} /><span>{category.name}</span></label>)}</div></fieldset>
          <label className="task-field task-field-wide">タグ<input className="input" value={draft.tags} onChange={(event) => setDraft({ ...draft, tags: event.target.value })} placeholder="カンマ区切り" disabled={!canWrite} /></label>
          <label className="task-field task-field-wide">メモ<textarea className="input" rows={7} value={draft.notes} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} disabled={!canWrite} /></label>
          <footer>
            <div>{task && canWrite && <button type="button" className="button button-danger" onClick={() => void onDelete(task)} disabled={saving}><Trash2 size={15} />削除</button>}</div>
            <div><button type="button" className="button" onClick={onClose}>閉じる</button>{canWrite && <button className="button button-primary" disabled={saving}>{saving ? "保存中…" : "保存"}</button>}</div>
          </footer>
        </form>
      </section>
    </div>
  );
}

export function TaskManagement({ view, getToken, canWrite, onViewChange }: {
  view: "board" | "timeline";
  getToken: TokenGetter;
  canWrite: boolean;
  onViewChange: (view: "board" | "timeline") => void;
}) {
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const tasksRef = useRef<TaskItem[]>([]);
  const updateVersionsRef = useRef(new Map<string, number>());
  const updateQueuesRef = useRef(new Map<string, Promise<unknown>>());
  const undoStackRef = useRef<TaskHistoryEntry[]>([]);
  const redoStackRef = useRef<TaskHistoryEntry[]>([]);
  const historyBusyRef = useRef(false);
  const [meta, setMeta] = useState<TaskMeta>(emptyMeta);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [projectId, setProjectId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [editor, setEditor] = useState<{ task: TaskItem | null; status: TaskStatus } | null>(null);
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false, busy: false });
  const [historyMessage, setHistoryMessage] = useState("");

  const changeTasks = useCallback((update: (current: TaskItem[]) => TaskItem[]) => {
    const next = update(tasksRef.current);
    tasksRef.current = next;
    setTasks(next);
  }, []);

  const syncHistoryState = useCallback(() => {
    setHistoryState({
      canUndo: undoStackRef.current.length > 0,
      canRedo: redoStackRef.current.length > 0,
      busy: historyBusyRef.current,
    });
  }, []);

  const clearHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    historyBusyRef.current = false;
    syncHistoryState();
  }, [syncHistoryState]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [taskData, metaData] = await Promise.all([
        apiRequest<{ tasks: TaskItem[] }>(getToken, "/api/tasks"),
        apiRequest<TaskMeta>(getToken, "/api/tasks/meta"),
      ]);
      const nextTasks = taskData.tasks ?? [];
      tasksRef.current = nextTasks;
      setTasks(nextTasks);
      setMeta(metaData);
      clearHistory();
    } catch {
      setError("タスクを読み込めませんでした。再読み込みしてください。");
    } finally {
      setLoading(false);
    }
  }, [clearHistory, getToken]);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("ja");
    return tasks.filter((task) =>
      (!projectId || task.projectId === projectId) &&
      (!categoryId || task.categoryIds.includes(categoryId)) &&
      (!query || `${task.title} ${task.notes} ${task.tags}`.toLocaleLowerCase("ja").includes(query))
    );
  }, [categoryId, projectId, search, tasks]);

  const commitTaskUpdate = useCallback(async (id: string, patch: Partial<TaskDraft>, recordHistory: boolean) => {
    if (!canWrite) return false;
    const previous = tasksRef.current.find((task) => task.id === id);
    if (!previous) return false;
    const historyPatches = createTaskHistoryPatches(previous, patch);
    if (!historyPatches) return true;
    const previousRedo = recordHistory ? redoStackRef.current : [];
    const historyEntry: TaskHistoryEntry | null = recordHistory ? {
      taskId: id,
      taskTitle: previous.title,
      ...historyPatches,
    } : null;
    if (historyEntry) {
      undoStackRef.current = [...undoStackRef.current, historyEntry].slice(-TASK_HISTORY_LIMIT);
      redoStackRef.current = [];
      syncHistoryState();
    }

    const version = (updateVersionsRef.current.get(id) ?? 0) + 1;
    updateVersionsRef.current.set(id, version);
    setError(null);
    changeTasks((current) => current.map((task) => task.id === id ? applyTaskPatch(task, patch) : task));
    const queuedRequest = (updateQueuesRef.current.get(id) ?? Promise.resolve())
      .catch(() => undefined)
      .then(() => apiRequest<{ task: TaskItem }>(getToken, `/api/tasks/${id}`, { method: "PATCH", body: JSON.stringify(patch) }));
    updateQueuesRef.current.set(id, queuedRequest);

    try {
      const response = await queuedRequest;
      if (updateVersionsRef.current.get(id) !== version) return true;
      if (patch.projectId !== undefined || patch.goalId !== undefined) {
        changeTasks((current) => current.map((task) => task.id === id ? response.task : task));
      }
      setEditor((current) => current?.task?.id === id ? { ...current, task: response.task } : current);
      return true;
    } catch {
      if (updateVersionsRef.current.get(id) !== version) return false;
      changeTasks((current) => current.map((task) => task.id === id ? previous : task));
      if (historyEntry) {
        undoStackRef.current = undoStackRef.current.filter((entry) => entry !== historyEntry);
        redoStackRef.current = previousRedo;
        syncHistoryState();
      }
      setError("タスクを更新できませんでした。入力内容を確認してください。");
      return false;
    } finally {
      if (updateVersionsRef.current.get(id) === version) updateVersionsRef.current.delete(id);
      if (updateQueuesRef.current.get(id) === queuedRequest) updateQueuesRef.current.delete(id);
    }
  }, [canWrite, changeTasks, getToken, syncHistoryState]);

  const updateTask = useCallback(async (id: string, patch: Partial<TaskDraft>) => {
    await commitTaskUpdate(id, patch, true);
  }, [commitTaskUpdate]);

  const undoTaskChange = useCallback(async () => {
    if (!canWrite || historyBusyRef.current) return;
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    redoStackRef.current.push(entry);
    historyBusyRef.current = true;
    syncHistoryState();
    const succeeded = await commitTaskUpdate(entry.taskId, entry.before, false);
    if (!succeeded) {
      redoStackRef.current = redoStackRef.current.filter((item) => item !== entry);
      undoStackRef.current.push(entry);
    } else {
      setHistoryMessage(`「${entry.taskTitle}」の変更を取り消しました`);
    }
    historyBusyRef.current = false;
    syncHistoryState();
  }, [canWrite, commitTaskUpdate, syncHistoryState]);

  const redoTaskChange = useCallback(async () => {
    if (!canWrite || historyBusyRef.current) return;
    const entry = redoStackRef.current.pop();
    if (!entry) return;
    undoStackRef.current.push(entry);
    historyBusyRef.current = true;
    syncHistoryState();
    const succeeded = await commitTaskUpdate(entry.taskId, entry.after, false);
    if (!succeeded) {
      undoStackRef.current = undoStackRef.current.filter((item) => item !== entry);
      redoStackRef.current.push(entry);
    } else {
      setHistoryMessage(`「${entry.taskTitle}」の変更をやり直しました`);
    }
    historyBusyRef.current = false;
    syncHistoryState();
  }, [canWrite, commitTaskUpdate, syncHistoryState]);

  useEffect(() => {
    function handleHistoryShortcut(event: KeyboardEvent) {
      if (!canWrite || editor || event.defaultPrevented || event.isComposing || isEditableTarget(event.target)) return;
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== "z") return;
      const action = event.shiftKey ? redoTaskChange : undoTaskChange;
      const available = event.shiftKey ? redoStackRef.current.length > 0 : undoStackRef.current.length > 0;
      if (!available || historyBusyRef.current) return;
      event.preventDefault();
      void action();
    }
    window.addEventListener("keydown", handleHistoryShortcut);
    return () => window.removeEventListener("keydown", handleHistoryShortcut);
  }, [canWrite, editor, redoTaskChange, undoTaskChange]);

  const editTask = useCallback((task: TaskItem) => setEditor({ task, status: task.status }), []);

  async function saveTask(draft: TaskDraft) {
    if (!canWrite) return;
    setSaving(true); setError(null);
    try {
      if (editor?.task) {
        const succeeded = await commitTaskUpdate(editor.task.id, draft, true);
        if (!succeeded) return;
      } else {
        const response = await apiRequest<{ task: TaskItem }>(getToken, "/api/tasks", { method: "POST", body: JSON.stringify(draft) });
        changeTasks((current) => [...current, response.task]);
      }
      setEditor(null);
    } catch {
      setError("タスクを保存できませんでした。必須項目と日付を確認してください。");
    } finally {
      setSaving(false);
    }
  }

  async function deleteTask(task: TaskItem) {
    if (!canWrite || !confirm(`「${task.title}」を削除しますか？`)) return;
    setSaving(true);
    try {
      await apiRequest(getToken, `/api/tasks/${task.id}`, { method: "DELETE" });
      changeTasks((current) => current.filter((item) => item.id !== task.id));
      undoStackRef.current = undoStackRef.current.filter((entry) => entry.taskId !== task.id);
      redoStackRef.current = redoStackRef.current.filter((entry) => entry.taskId !== task.id);
      syncHistoryState();
      setEditor(null);
    } catch {
      setError("タスクを削除できませんでした。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <header className="task-page-header">
        <div><p className="eyebrow">TASKS</p><h1 tabIndex={-1}>タスク管理</h1><p>タスクの状態と日程を、ボードとタイムラインの両方で管理します。</p></div>
        {canWrite && <div><button className="button button-primary" onClick={() => setEditor({ task: null, status: "not_started" })}><Plus size={16} />タスクを追加</button></div>}
      </header>
      <div className="task-view-row">
        <nav className="task-view-tabs" aria-label="タスク表示">
          <button className={view === "board" ? "active" : ""} onClick={() => onViewChange("board")} aria-current={view === "board" ? "page" : undefined}><Columns3 size={16} />ボード</button>
          <button className={view === "timeline" ? "active" : ""} onClick={() => onViewChange("timeline")} aria-current={view === "timeline" ? "page" : undefined}><CalendarDays size={16} />タイムライン</button>
        </nav>
        {canWrite && (
          <div className="task-history-controls" aria-label="変更履歴">
            <button type="button" onClick={() => void undoTaskChange()} disabled={!historyState.canUndo || historyState.busy} aria-label="直前の変更を取り消す" title="取り消す（⌘Z）"><Undo2 size={15} aria-hidden="true" /><span>戻す</span><kbd>⌘Z</kbd></button>
            <button type="button" onClick={() => void redoTaskChange()} disabled={!historyState.canRedo || historyState.busy} aria-label="取り消した変更をやり直す" title="やり直す（⌘⇧Z）"><Redo2 size={15} aria-hidden="true" /><span>やり直す</span><kbd>⌘⇧Z</kbd></button>
          </div>
        )}
      </div>
      <section className="task-toolbar" aria-label="タスクの絞り込み">
        <label className="task-search"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="タイトル・メモ・タグを検索" /></label>
        <label><span>プロジェクト</span><select value={projectId} onChange={(event) => setProjectId(event.target.value)}><option value="">すべて</option>{meta.projects.map((project) => <option value={project.id} key={project.id}>{project.name}</option>)}</select></label>
        <label><span>分類</span><select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}><option value="">すべて</option>{meta.categories.map((category) => <option value={category.id} key={category.id}>{category.name}</option>)}</select></label>
        <strong>{filtered.length}<small>件</small></strong>
      </section>
      {error && <p className="error-banner" role="alert">{error}<button onClick={load}>再読み込み</button></p>}
      {loading ? <div className="task-loading"><span className="spinner" />タスクを読み込んでいます</div> : view === "board"
        ? <TaskBoard tasks={filtered} meta={meta} canWrite={canWrite} onEdit={editTask} onUpdate={updateTask} />
        : <TaskTimeline tasks={filtered} canWrite={canWrite} onEdit={editTask} onUpdate={updateTask} />}
      {editor && <TaskEditor key={editor.task?.id ?? `new-${editor.status}`} task={editor.task} initialStatus={editor.status} meta={meta} canWrite={canWrite} saving={saving} onClose={() => setEditor(null)} onSave={saveTask} onDelete={deleteTask} />}
      <p className="sr-only" role="status" aria-live="polite">{historyMessage}</p>
    </>
  );
}
